import type { ResidentBalanceInfo } from './ResidentBalanceInfo';

export interface ManualPaymentAmountsResult {
  minAmountCents: number;
  maxAmountCents: number;
  initialAmountCents: number;
  isInSettlement: boolean;
}

/**
 * Calculates min, max, and initial amounts for manual payment forms.
 *
 * For settlement residents: uses lower min amount, targets 0€ balance, allows 0€ initial.
 * For active residents: uses regular min amount, targets topUpAmount, clamps to min.
 *
 * All amounts are in cents. Values come from API (balanceInfo).
 *
 * @example
 * const result = calculateManualPaymentAmounts(balanceInfo, 15000);
 * // result.initialAmountCents = 5000 (50€ to reach 200€ topUpAmount)
 */
export function calculateManualPaymentAmounts(
  balanceInfo: ResidentBalanceInfo,
  residentBalanceCents: number | undefined
): ManualPaymentAmountsResult {
  const isInSettlement = balanceInfo.residentConfiguration.isInSettlement;

  // topUpAmount from API is in euros, convert to cents
  const topUpCents = balanceInfo.residentConfiguration.topUpAmount * 100;

  const minCents = isInSettlement
    ? balanceInfo.nursingHomeConfiguration.inSettlementMinPaymentAmount
    : balanceInfo.nursingHomeConfiguration.minPaymentAmount;

  const maxCents = balanceInfo.nursingHomeConfiguration.maxPaymentAmount;

  // Target: 0€ for settlement, topUpAmount for active
  const targetCents = isInSettlement ? 0 : topUpCents;

  const suggestedCents =
    residentBalanceCents !== undefined
      ? targetCents - residentBalanceCents
      : minCents;

  // Clamp: settlement allows 0 (no payment needed), active clamps to min
  const initialCents = isInSettlement
    ? Math.max(0, Math.min(maxCents, suggestedCents))
    : Math.min(maxCents, Math.max(minCents, suggestedCents));

  return {
    minAmountCents: minCents,
    maxAmountCents: maxCents,
    initialAmountCents: initialCents,
    isInSettlement,
  };
}
